// crewbit-admin/src/components/money/AuthoringLineItemsSection.tsx
//
// Spec: §InvoiceAuthoring.7.2 Step 2 (admin parallel). Section card with
// line-item table + "+ Add line" affordance + tax rate input + sticky
// totals row at section bottom.
//
// Mirrors crewbit-mobile/components/money/authoring/Step2LineItems.tsx +
// LineItemRow.tsx — admin folds the row into this file (flat folder per §2.3).
//
// Boundary semantics (Hard rules #9, #10):
//   - State stores dollars-as-string + tax percent (UI readability — "13" for 13%)
//   - useInvoiceDraft converts to cents + decimal at the RPC boundary
//   - Every numeric uses font-variant-numeric: tabular-nums

import { Minus, Plus, Trash2 } from 'lucide-react';
import { MoneyInput } from '../common/MoneyInput';
import { addMoney, formatMoney, multiplyMoney } from '../../lib/money';
import {
  subtotalDollars,
  taxDollars,
  type AuthoringLine,
} from '../../lib/invoiceLineItems';
import { palette, radii, spacing, typography } from '../../styles/tokens';
import { resolveTaxRatePercent, type AuthoringDraftState } from '../../hooks/useInvoiceDraft';

interface Props {
  state: AuthoringDraftState;
  onChange: (patch: Partial<AuthoringDraftState>) => void;
}

function makeBlankLine(): AuthoringLine {
  return {
    id: `line-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
    description: '',
    quantity: 1,
    unitPriceDollars: '',
    amountDollars: '0.00',
    kind: null,
  };
}

export function AuthoringLineItemsSection({ state, onChange }: Props) {
  const onLineChange = (idx: number, line: AuthoringLine) => {
    const lines = [...state.lines];
    lines[idx] = line;
    onChange({ lines });
  };

  const onLineDelete = (idx: number) => {
    const lines = state.lines.filter((_, i) => i !== idx);
    onChange({ lines: lines.length > 0 ? lines : [makeBlankLine()] });
  };

  const onAddLine = () => onChange({ lines: [...state.lines, makeBlankLine()] });

  const onTaxChange = (raw: string) => {
    const cleaned = raw.replace(/[^\d.]/g, '');
    if (cleaned === '') {
      // Clearing the field releases the override; server resolves on save.
      onChange({ taxRateOverride: null });
      return;
    }
    const num = Number(cleaned);
    if (Number.isNaN(num)) return;
    onChange({ taxRateOverride: Math.max(0, Math.min(100, num)) });
  };

  const displayTaxPercent = resolveTaxRatePercent(state);
  const subtotal = subtotalDollars(state.lines);
  const tax = taxDollars(subtotal, displayTaxPercent);
  const total = addMoney(subtotal, tax);

  return (
    <section style={CARD}>
      <div style={HEADER_ROW}>
        <div>
          <div style={STEP_LABEL}>Step 2 of 3</div>
          <div style={SECTION_TITLE}>Line items</div>
        </div>
      </div>

      <div style={LINES_LIST}>
        {state.lines.map((line, idx) => (
          <LineItemRow
            key={line.id}
            line={line}
            onChange={(l) => onLineChange(idx, l)}
            onDelete={() => onLineDelete(idx)}
          />
        ))}
      </div>

      <button type="button" onClick={onAddLine} style={ADD_BTN}>
        <Plus size={15} color={palette.accent} strokeWidth={2} />
        Add line
      </button>

      <div style={TAX_ROW}>
        <span style={TAX_LABEL}>Tax rate</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type="text"
            inputMode="decimal"
            value={String(displayTaxPercent)}
            onChange={(e) => onTaxChange(e.target.value)}
            aria-label="Tax rate percent"
            style={TAX_INPUT}
          />
          <span style={TAX_PERCENT}>%</span>
        </div>
      </div>

      <div style={TOTALS_BLOCK}>
        <TotalRow label="Subtotal" value={formatMoney(subtotal)} />
        <TotalRow label={`Tax (${displayTaxPercent}%)`} value={formatMoney(tax)} />
        <TotalRow label="Total" value={formatMoney(total)} strong />
      </div>
    </section>
  );
}

function TotalRow({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div style={TOTAL_ROW}>
      <span
        style={{
          ...TOTAL_LABEL,
          fontWeight: strong ? typography.weight.semibold : typography.weight.regular,
          color: strong ? palette.g800 : palette.g600,
        }}
      >
        {label}
      </span>
      <span
        style={{
          ...TOTAL_VALUE,
          fontWeight: strong ? typography.weight.bold : typography.weight.regular,
          color: strong ? palette.g800 : palette.g700,
        }}
      >
        {value}
      </span>
    </div>
  );
}

interface RowProps {
  line: AuthoringLine;
  onChange: (line: AuthoringLine) => void;
  onDelete: () => void;
}

function LineItemRow({ line, onChange, onDelete }: RowProps) {
  const recompute = (qty: number, unit: string): string => multiplyMoney(unit || '0', qty);

  const setDescription = (description: string) => onChange({ ...line, description });

  const setQuantity = (q: number) => {
    const quantity = Math.max(1, q);
    onChange({
      ...line,
      quantity,
      amountDollars: recompute(quantity, line.unitPriceDollars),
    });
  };

  const setUnit = (unitPriceDollars: string) => {
    onChange({
      ...line,
      unitPriceDollars,
      amountDollars: recompute(line.quantity, unitPriceDollars),
    });
  };

  return (
    <div style={ROW}>
      <input
        type="text"
        value={line.description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description"
        style={DESC_INPUT}
      />

      <div style={ROW_FIELDS}>
        <div style={{ ...FIELD_GROUP, width: 110 }}>
          <div style={FIELD_LABEL}>Qty</div>
          <div style={STEPPER}>
            <button
              type="button"
              onClick={() => setQuantity(line.quantity - 1)}
              disabled={line.quantity <= 1}
              style={{ ...STEP_BTN, opacity: line.quantity <= 1 ? 0.3 : 1 }}
              aria-label="Decrease quantity"
            >
              <Minus size={13} color={palette.g700} strokeWidth={2} />
            </button>
            <span style={QTY_VALUE}>{line.quantity}</span>
            <button
              type="button"
              onClick={() => setQuantity(line.quantity + 1)}
              style={STEP_BTN}
              aria-label="Increase quantity"
            >
              <Plus size={13} color={palette.g700} strokeWidth={2} />
            </button>
          </div>
        </div>

        <div style={{ ...FIELD_GROUP, flex: 1, minWidth: 110 }}>
          <div style={FIELD_LABEL}>Unit price</div>
          <MoneyInput value={line.unitPriceDollars} onChange={setUnit} placeholder="0.00" />
        </div>

        <div style={{ ...FIELD_GROUP, width: 110, alignItems: 'flex-end' }}>
          <div style={FIELD_LABEL}>Amount</div>
          <span style={AMOUNT_VALUE}>{formatMoney(line.amountDollars)}</span>
        </div>

        <button
          type="button"
          onClick={onDelete}
          aria-label="Delete line"
          style={DELETE_BTN}
        >
          <Trash2 size={15} color={palette.red} strokeWidth={1.8} />
        </button>
      </div>
    </div>
  );
}

const CARD: React.CSSProperties = {
  background: palette.white,
  border: `1px solid ${palette.g100}`,
  borderRadius: radii.md,
  padding: spacing.md,
  marginBottom: spacing.md,
};

const HEADER_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: spacing.md,
};

const STEP_LABEL: React.CSSProperties = {
  fontSize: typography.size.micro,
  fontWeight: typography.weight.semibold,
  color: palette.g500,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const SECTION_TITLE: React.CSSProperties = {
  fontSize: typography.size.heading,
  fontWeight: typography.weight.semibold,
  color: palette.g800,
  marginTop: 2,
};

const LINES_LIST: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.sm,
};

const ROW: React.CSSProperties = {
  background: palette.white,
  border: `1px solid ${palette.g100}`,
  borderRadius: radii.sm,
  padding: spacing.sm,
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.sm,
};

const DESC_INPUT: React.CSSProperties = {
  background: palette.g50,
  border: `1px solid ${palette.g100}`,
  borderRadius: radii.xs,
  padding: `${spacing.xs}px ${spacing.sm}px`,
  fontSize: typography.size.body,
  color: palette.g800,
  fontFamily: typography.fontFamily,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const ROW_FIELDS: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: spacing.sm,
  flexWrap: 'wrap',
};

const FIELD_GROUP: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
};

const FIELD_LABEL: React.CSSProperties = {
  fontSize: typography.size.micro,
  fontWeight: typography.weight.medium,
  color: palette.g500,
};

const STEPPER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: palette.g100,
  borderRadius: radii.sm,
  padding: '0 6px',
  height: 36,
  width: '100%',
};

const STEP_BTN: React.CSSProperties = {
  width: 24,
  height: 24,
  border: 'none',
  background: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
};

const QTY_VALUE: React.CSSProperties = {
  fontSize: typography.size.body,
  color: palette.g800,
  fontVariantNumeric: 'tabular-nums',
};

const AMOUNT_VALUE: React.CSSProperties = {
  fontSize: typography.size.body,
  fontWeight: typography.weight.semibold,
  color: palette.g800,
  fontVariantNumeric: 'tabular-nums',
  paddingTop: 9,
  paddingBottom: 9,
  textAlign: 'right',
};

const DELETE_BTN: React.CSSProperties = {
  width: 32,
  height: 36,
  border: 'none',
  background: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
  flexShrink: 0,
};

const ADD_BTN: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  width: '100%',
  marginTop: spacing.md,
  padding: `${spacing.sm}px 0`,
  borderRadius: radii.sm,
  border: `1px dashed ${palette.accentSoft}`,
  background: palette.white,
  fontSize: typography.size.body,
  fontWeight: typography.weight.semibold,
  color: palette.accent,
  cursor: 'pointer',
  fontFamily: typography.fontFamily,
};

const TAX_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: spacing.lg,
  padding: `${spacing.sm}px ${spacing.md}px`,
  background: palette.white,
  borderRadius: radii.sm,
  border: `1px solid ${palette.g100}`,
};

const TAX_LABEL: React.CSSProperties = {
  fontSize: typography.size.body,
  color: palette.g700,
};

const TAX_INPUT: React.CSSProperties = {
  width: 60,
  textAlign: 'right',
  fontSize: typography.size.body,
  color: palette.g800,
  fontFamily: typography.fontFamily,
  fontVariantNumeric: 'tabular-nums',
  border: `1px solid ${palette.g200}`,
  borderRadius: radii.xs,
  padding: '6px 8px',
  outline: 'none',
};

const TAX_PERCENT: React.CSSProperties = {
  fontSize: typography.size.body,
  color: palette.g500,
};

const TOTALS_BLOCK: React.CSSProperties = {
  marginTop: spacing.md,
  paddingTop: spacing.sm,
  borderTop: `1px solid ${palette.g100}`,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const TOTAL_ROW: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const TOTAL_LABEL: React.CSSProperties = {
  fontSize: typography.size.body,
};

const TOTAL_VALUE: React.CSSProperties = {
  fontSize: typography.size.body,
  fontVariantNumeric: 'tabular-nums',
};
